import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiClient, FrontendAuthError, getHttpStatus } from '../api/client';
import {
  clearLegacyAuthState,
  clearLegacyAuthStorage,
  setCurrentUserSnapshot,
  subscribeCurrentUser,
  type AuthUser,
} from './session';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'error';

interface AuthContextValue {
  status: AuthStatus;
  currentUser: AuthUser | null;
  error: string | null;
  refresh: () => Promise<AuthUser | null>;
  login: (username: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
}

interface AuthEnvelope {
  data?: AuthUser;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function responseUser(response: unknown): AuthUser {
  const user = (response as AuthEnvelope | undefined)?.data;
  if (!user || typeof user.user_id !== 'number' || !user.role) {
    throw new Error('认证响应缺少用户信息');
  }
  return user;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [error, setError] = useState<string | null>(null);

  const acceptUser = useCallback((user: AuthUser | null) => {
    setCurrentUser(user);
    setCurrentUserSnapshot(user);
    setStatus(user ? 'authenticated' : 'unauthenticated');
  }, []);

  const refresh = useCallback(async (): Promise<AuthUser | null> => {
    clearLegacyAuthStorage();
    setStatus('loading');
    setError(null);
    try {
      const response = await apiClient.get<unknown, AuthEnvelope>('/api/auth/me');
      const user = responseUser(response);
      acceptUser(user);
      return user;
    } catch (requestError) {
      if (getHttpStatus(requestError) === 401) {
        clearLegacyAuthState();
        setCurrentUser(null);
        setStatus('unauthenticated');
        return null;
      }
      clearLegacyAuthState();
      setCurrentUser(null);
      setStatus('error');
      setError('无法确认登录状态，请检查网络后重试');
      return null;
    }
  }, [acceptUser]);

  const login = useCallback(async (username: string, password: string): Promise<AuthUser> => {
    clearLegacyAuthState();
    setStatus('loading');
    setError(null);
    try {
      await apiClient.post('/api/auth/login', { username, password });
      const response = await apiClient.get<unknown, AuthEnvelope>('/api/auth/me');
      const user = responseUser(response);
      acceptUser(user);
      return user;
    } catch (requestError) {
      clearLegacyAuthState();
      setCurrentUser(null);
      setStatus('unauthenticated');
      throw requestError;
    }
  }, [acceptUser]);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiClient.post('/api/auth/logout');
      clearLegacyAuthState();
      setCurrentUser(null);
      setStatus('unauthenticated');
    } catch (requestError) {
      if (getHttpStatus(requestError) === 401) {
        clearLegacyAuthState();
        setCurrentUser(null);
        setStatus('unauthenticated');
        return;
      }
      if (
        requestError instanceof FrontendAuthError
        && requestError.code === 'csrf_cookie_missing'
      ) {
        try {
          await apiClient.get('/api/auth/me');
        } catch (meError) {
          if (getHttpStatus(meError) === 401) {
            clearLegacyAuthState();
            setCurrentUser(null);
            setStatus('unauthenticated');
            return;
          }
        }
      }
      throw requestError;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeCurrentUser((user) => {
      if (user === null) {
        setCurrentUser(null);
        setStatus((previous) => previous === 'loading' ? previous : 'unauthenticated');
      }
    });
    queueMicrotask(() => void refresh());
    return unsubscribe;
  }, [refresh]);

  const value = useMemo<AuthContextValue>(() => ({
    status,
    currentUser,
    error,
    refresh,
    login,
    logout,
  }), [status, currentUser, error, refresh, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth 必须在 AuthProvider 内使用');
  return context;
}
